import React, { useState, useEffect } from 'react';
import { suppliersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Search, Edit, Trash2, Phone, Mail, Building2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = {
  name: '',
  supplier_type: 'Insurance Builder',
  contact_name: '',
  email: '',
  phone: '',
  address: '',
  email_domains: '',
  notes: '',
  payment_terms_days: '',
};

export const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [supplierTypes, setSupplierTypes] = useState(['Insurance Builder', 'Builder', 'Other']);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchSuppliers();
    suppliersAPI.getTypes()
      .then((res) => setSupplierTypes(res.data.supplier_types || supplierTypes))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await suppliersAPI.getAll();
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to fetch suppliers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setEditing(null);
    setFormData(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Supplier name is required');
      return;
    }
    const payload = {
      ...formData,
      email_domains: formData.email_domains
        ? formData.email_domains.split(',').map((d) => d.trim().toLowerCase()).filter(Boolean)
        : [],
      payment_terms_days: formData.payment_terms_days === '' || formData.payment_terms_days == null
        ? null : parseInt(formData.payment_terms_days, 10),
    };
    try {
      if (editing) {
        await suppliersAPI.update(editing.id, payload);
        toast.success('Supplier updated');
      } else {
        await suppliersAPI.create(payload);
        toast.success('Supplier added');
      }
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editing ? 'Failed to update' : 'Failed to add'));
    }
  };

  const handleEdit = (supplier) => {
    setEditing(supplier);
    setFormData({
      name: supplier.name || '',
      supplier_type: supplier.supplier_type || 'Insurance Builder',
      contact_name: supplier.contact_name || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      email_domains: (supplier.email_domains || []).join(', '),
      notes: supplier.notes || '',
      payment_terms_days: supplier.payment_terms_days ?? '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await suppliersAPI.delete(id);
      toast.success('Supplier deleted');
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const filtered = suppliers.filter((s) => {
    const q = searchTerm.toLowerCase();
    return (
      s.name.toLowerCase().includes(q) ||
      (s.supplier_type || '').toLowerCase().includes(q) ||
      (s.contact_name || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q)
    );
  });

  const inputCls = 'bg-input border-border rounded-lg';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Suppliers</h1>
          <p className="text-muted-foreground mt-1">Builders &amp; insurers that send you purchase orders</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="font-semibold rounded-lg" data-testid="add-supplier-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Supplier
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Supplier Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className={inputCls} placeholder="e.g., AllInsure Builders" data-testid="supplier-name" />
                </div>
                <div className="space-y-2">
                  <Label>Supplier Type</Label>
                  <select
                    value={formData.supplier_type}
                    onChange={(e) => setFormData({ ...formData, supplier_type: e.target.value })}
                    className="w-full h-10 px-3 bg-input border border-border rounded-lg text-sm"
                    data-testid="supplier-type"
                  >
                    {supplierTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name</Label>
                  <Input value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} className={inputCls} placeholder="e.g., Sarah Lee" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className={inputCls} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputCls} placeholder="po@supplier.com" data-testid="supplier-email" />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className={inputCls} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>PO Email Domains</Label>
                <Input value={formData.email_domains} onChange={(e) => setFormData({ ...formData, email_domains: e.target.value })} className={inputCls} placeholder="allinsure.com, poteam.allinsure.com" />
                <p className="text-xs text-muted-foreground">Comma-separated. Used by AI email scanning to auto-match incoming POs to this supplier.</p>
              </div>

              <div className="space-y-2">
                <Label>Payment terms (days)</Label>
                <Input type="number" min="0" max="180" value={formData.payment_terms_days} onChange={(e) => setFormData({ ...formData, payment_terms_days: e.target.value })} className={inputCls} placeholder="e.g., 30" data-testid="supplier-payment-terms" />
                <p className="text-xs text-muted-foreground">How many days after the invoice date this supplier pays. Used to auto-set invoice due dates &amp; overdue alerts. Blank = 30 days.</p>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className={inputCls} rows={3} />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-lg">Cancel</Button>
                <Button type="submit" className="font-semibold rounded-lg" data-testid="supplier-submit">{editing ? 'Update' : 'Add Supplier'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search suppliers..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-input border-border rounded-lg" data-testid="supplier-search" />
      </div>

      {/* Table */}
      <Card className="bg-card border-border rounded-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Building2 className="h-12 w-12 mb-4" />
              <p>No suppliers yet</p>
              <p className="text-sm">Add the builders &amp; insurers that send you work.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead>Supplier</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Jobs</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => (
                  <TableRow key={s.id} className="border-border" data-testid={`supplier-row-${s.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{s.name}</p>
                        {s.contact_name && <p className="text-xs text-muted-foreground">{s.contact_name}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="rounded-lg text-xs">{s.supplier_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {s.email && (
                          <a href={`mailto:${s.email}`} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
                            <Mail className="h-3 w-3" />{s.email}
                          </a>
                        )}
                        {s.phone && (
                          <a href={`tel:${s.phone}`} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
                            <Phone className="h-3 w-3" />{s.phone}
                          </a>
                        )}
                        {!s.email && !s.phone && <span className="text-muted-foreground text-xs">-</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 font-mono text-sm">
                        <Briefcase className="h-3 w-3 text-muted-foreground" />{s.job_count ?? 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(s)} data-testid={`edit-supplier-${s.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)} className="text-destructive" data-testid={`delete-supplier-${s.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuppliersPage;
